import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  InputAdornment,
  InputBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tools } from '../config/tools';
import { pagePath } from '../seo/seoData';
import { toolAccent } from '../theme';
import { useRecents } from '../hooks/useToolPrefs';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  lang: string;
}

// Lowercase and strip diacritics so "sifre" finds "şifre" and "donusturucu" finds "dönüştürücü".
const normalize = (value: string) =>
  value
    .toLocaleLowerCase('tr')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ı/g, 'i');

const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose, lang }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const recents = useRecents();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  // Index the localized texts plus the English title, so English keywords work in every language.
  const index = useMemo(
    () =>
      tools.map((tool) => ({
        tool,
        haystack: normalize(
          [
            t(`tools.${tool.id}.title`),
            t(`tools.${tool.id}.shortTitle`),
            t(`tools.${tool.id}.description`),
            i18n.getFixedT('en')(`tools.${tool.id}.title`),
            tool.slug.replace(/-/g, ' '),
          ].join(' ')
        ),
      })),
    [t, i18n]
  );

  const results = useMemo(() => {
    const terms = normalize(query).split(/\s+/).filter(Boolean);
    if (!terms.length) {
      // Recently used tools first, then the rest in menu order.
      return [...tools].sort((a, b) => {
        const ra = recents.indexOf(a.id);
        const rb = recents.indexOf(b.id);
        return (ra === -1 ? Infinity : ra) - (rb === -1 ? Infinity : rb);
      });
    }
    return index.filter(({ haystack }) => terms.every((term) => haystack.includes(term))).map(({ tool }) => tool);
  }, [query, index, recents]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const go = (slug: string) => {
    navigate(pagePath(lang, slug));
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter' && results[activeIndex]) {
      event.preventDefault();
      go(results[activeIndex].slug);
    }
  };

  useEffect(() => {
    document.getElementById(`palette-item-${activeIndex}`)?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { alignSelf: 'flex-start', mt: { xs: 2, sm: 10 } } }}
    >
      <InputBase
        autoFocus
        fullWidth
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={t('layout.searchPlaceholder')}
        inputProps={{
          'aria-label': t('layout.search'),
          role: 'combobox',
          'aria-expanded': true,
          'aria-controls': 'palette-results',
          'aria-activedescendant': results.length ? `palette-item-${activeIndex}` : undefined,
        }}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
        sx={{ px: 2, py: 1.5, fontSize: 17, borderBottom: 1, borderColor: 'divider' }}
      />
      <List id="palette-results" role="listbox" dense sx={{ maxHeight: 420, overflowY: 'auto', py: 1 }}>
        {results.map((tool, i) => {
          const Icon = tool.icon;
          return (
            <ListItemButton
              key={tool.id}
              id={`palette-item-${i}`}
              role="option"
              aria-selected={i === activeIndex}
              selected={i === activeIndex}
              onMouseMove={() => setActiveIndex(i)}
              onClick={() => go(tool.slug)}
              sx={{ mx: 1, borderRadius: 1.5 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Icon sx={{ color: toolAccent(tool.color, theme.palette.mode) }} />
              </ListItemIcon>
              <ListItemText
                primary={t(`tools.${tool.id}.title`)}
                secondary={t(`tools.${tool.id}.description`)}
                primaryTypographyProps={{ fontWeight: 600 }}
                secondaryTypographyProps={{ noWrap: true }}
              />
            </ListItemButton>
          );
        })}
        {!results.length && (
          <Typography color="text.secondary" sx={{ px: 3, py: 2 }}>
            {t('layout.noResults')}
          </Typography>
        )}
      </List>
      <Box sx={{ px: 2, py: 1, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 2 }}>
        <Typography variant="caption" color="text.secondary">
          ↑↓ {t('layout.navigate')} · ↵ {t('layout.open')} · Esc {t('layout.close')}
        </Typography>
      </Box>
    </Dialog>
  );
};

export default CommandPalette;
